import { useEffect, useRef, useState, type PointerEvent } from "react";
import { aspectCrop, clampCrop, keyboardCrop, moveCrop, resizeCrop, type CropAspect, type CropBox } from "../lib/crop";
import { Spinner } from "./Spinner";

interface PhotoCropProps {
  src: string;
  wide: boolean;
  top: number;
  left: number;
  slotAspect: number;
  previous?: CropBox;
  onSave: (crop: CropBox) => Promise<void>;
  onCancel: () => void;
}

type Preset = "fit" | "original" | "square" | "wide";
type Corner = "nw" | "ne" | "sw" | "se";

export function PhotoCrop({ src, wide, top, left, slotAspect, previous, onSave, onCancel }: PhotoCropProps) {
  const dialog = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; crop: CropBox; corner?: Corner } | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [preset, setPreset] = useState<Preset>(previous ? "fit" : "fit");
  const [crop, setCrop] = useState<CropBox | null>(previous ?? null);
  const [status, setStatus] = useState<"idle" | "saving" | "failed">("idle");
  const aspect: CropAspect = preset === "fit" ? slotAspect : preset === "original" ? dimensions ? dimensions.width / dimensions.height : null : preset === "square" ? 1 : 16 / 9;
  useEffect(() => {
    const image = new Image();
    if (new URL(src, window.location.href).origin !== window.location.origin) image.crossOrigin = "anonymous";
    image.onload = () => {
      setDimensions({ width: image.naturalWidth, height: image.naturalHeight });
      setCrop((current) => current ? clampCrop(current, image.naturalWidth, image.naturalHeight) : aspectCrop(slotAspect, image.naturalWidth, image.naturalHeight));
    };
    image.src = src;
  }, [src, slotAspect]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onCancel(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onCancel]);
  useEffect(() => {
    if (!wide || !dialog.current) return;
    const frame = dialog.current.closest<HTMLElement>(".bw-frame");
    if (!frame) return;
    const frameBox = frame.getBoundingClientRect(); const box = dialog.current.getBoundingClientRect();
    dialog.current.style.left = `${Math.max(8, Math.min(left, frameBox.width - box.width - 8))}px`;
    dialog.current.style.top = `${Math.max(8, Math.min(top, frameBox.height - box.height - 8))}px`;
  }, [left, top, wide, dimensions]);
  const choose = (next: Preset) => {
    if (!dimensions) return;
    setPreset(next);
    if (next === "original") setCrop({ x: 0, y: 0, width: 1, height: 1 });
    else setCrop(aspectCrop(next === "fit" ? slotAspect : next === "square" ? 1 : 16 / 9, dimensions.width, dimensions.height));
  };
  const pointer = (event: PointerEvent<HTMLElement>, corner?: Corner) => {
    if (!crop) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, crop, corner };
  };
  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current || !dimensions) return;
    const rect = event.currentTarget.getBoundingClientRect(); const start = drag.current;
    const dx = (event.clientX - start.x) / rect.width; const dy = (event.clientY - start.y) / rect.height;
    setCrop(start.corner ? resizeCrop(start.crop, start.corner, dx, dy, dimensions.width, dimensions.height, aspect) : moveCrop(start.crop, dx, dy, dimensions.width, dimensions.height));
  };
  const save = async () => {
    if (!crop || status === "saving") return;
    setStatus("saving");
    try { await onSave(crop); } catch { setStatus("failed"); }
  };
  return (
    <div ref={dialog} className={`bw-photo-crop ${wide ? "bw-photo-crop--popover" : "bw-photo-crop--sheet"}`} role="dialog" aria-modal={!wide} aria-label="Crop photo" style={wide ? { top, left } : undefined}>
      {!wide && <div className="bw-sheet-grip" aria-hidden="true" />}
      <h3>Crop photo</h3>
      <div className="bw-crop-canvas" onPointerMove={move} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}>
        <img src={src} alt="" />
        {crop && dimensions ? <button type="button" className="bw-crop-box" style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` }} aria-label="Crop box. Arrow keys move it. Shift and arrow keys resize it." onPointerDown={pointer} onKeyDown={(event) => {
          if (!(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"] as string[]).includes(event.key)) return;
          event.preventDefault(); setCrop(keyboardCrop(crop, event.key as "ArrowLeft", event.shiftKey, dimensions.width, dimensions.height, aspect));
        }}>
          {(["nw", "ne", "sw", "se"] as Corner[]).map((corner) => <span key={corner} className={`bw-crop-handle bw-crop-handle--${corner}`} onPointerDown={(event) => { event.stopPropagation(); pointer(event, corner); }} />)}
        </button> : <span className="bw-crop-loading">Loading photo…</span>}
      </div>
      <div className="bw-crop-presets" role="radiogroup" aria-label="Crop shape">
        {([ ["fit", "Fit the page"], ["original", "Original"], ["square", "Square"], ["wide", "Wide"] ] as Array<[Preset, string]>).map(([value, label]) => <button key={value} type="button" role="radio" aria-checked={preset === value} className={preset === value ? "is-selected" : undefined} onClick={() => choose(value)}>{label}</button>)}
      </div>
      {status === "failed" && <p className="bw-crop-error" role="alert">The crop wasn’t saved. Try again.</p>}
      <div className="bw-crop-actions"><button type="button" className="bw-btn bw-btn--quiet" disabled={status === "saving"} onClick={onCancel}>Cancel</button><button type="button" className="bw-btn bw-btn--primary" disabled={!crop || status === "saving"} onClick={() => void save()}>{status === "saving" && <Spinner />}{status === "saving" ? "Saving…" : "Save crop"}</button></div>
    </div>
  );
}
